import { Box, Button, Chip, Typography, useMediaQuery } from '@mui/material'
import { AddOutlined, ChevronRight } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import CrpReportService from '../../../../shared/services/crp-report/crp-report.service'
import { ICrpReport } from '../../../../shared/services/crp-report/crp-report.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { LoadingPage } from '../../loading-page'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'

export const ReportingCrpPage = () => {
  const crpReportService = new CrpReportService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const navigate = useNavigate()
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'))

  const [reports, setReports] = useState<ICrpReport[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [perPage, setPerPage] = useState(100)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    crpReportService.getAll()
      .then((response) => {
        setReports(response)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setReports([])
        setIsLoading(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error loading your plans, please try again', backgroundColor: 'text.primary' })
      })
  }, [])

  const periodLabel = (report: ICrpReport, key: 'baselineReportPeriod' | 'currentReportPeriod') => {
    // The baseline may instead be an archived (manually entered) base year, held on its own association.
    const period = key === 'baselineReportPeriod'
      ? (report.baselineReportPeriod || report.baselineArchivedReportPeriod)
      : report[key]
    if (!period) return '—'
    return period.friendlyName || period.calendarYear || '—'
  }

  const headCells: IHeadCell[] = [
    { id: 'created', numeric: false, label: 'Created', sortable: false, isHeading: true },
    { id: 'type', numeric: false, label: 'Type', sortable: false },
    { id: 'baseline', numeric: false, label: 'Baseline year', sortable: false },
    { id: 'reporting', numeric: false, label: 'Reporting year', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
    { id: 'action', numeric: false, align: 'right', label: '', sortable: false },
  ]

  // A generated plan opens read-only on the detail page; a draft resumes the wizard.
  const openReport = (report: ICrpReport) => {
    navigate(report.status === 'GENERATED'
      ? `/reporting/carbon-reduction-plan/${report.id}`
      : `/reporting/carbon-reduction-plan/${report.id}/edit`)
  }

  const handleSelected = (selectedRowIds: string[]) => {
    const report = reports.find((candidate) => candidate.id === selectedRowIds[0])
    if (report) openReport(report)
  }

  const rows = reports.map((report) => ({
    id: report.id,
    created: moment(report.createdAt).format('DD/MM/YYYY'),
    type: report.planType === 'PROGRESS' ? 'Progress report' : 'Carbon Reduction Plan',
    baseline: periodLabel(report, 'baselineReportPeriod'),
    reporting: report.planType === 'PROGRESS' ? 'All years since baseline' : periodLabel(report, 'currentReportPeriod'),
    status: (
      <Chip
        size='small'
        label={report.status === 'GENERATED' ? 'Generated' : 'Draft'}
        color={report.status === 'GENERATED' ? 'primary' : 'default'}
        variant={report.status === 'GENERATED' ? 'filled' : 'outlined'}
      />
    ),
    action: (
      <Button
        variant='outlined'
        endIcon={<ChevronRight />}
        // The whole row also opens the plan; stop the bubble so one click doesn't navigate twice.
        onClick={(event) => {
          event.stopPropagation()
          openReport(report)
        }}
        sx={{ textTransform: 'none' }}
      >
        {report.status === 'GENERATED' ? 'View' : 'Continue'}
      </Button>
    ),
  }))

  return (
    <PageOuterContainer>
      <PageHeading
        title='Carbon Reduction Plan (PPN 006)'
        includeBackButton={true}
        backButtonLink='/reporting'
        backButtonText='Back to reporting'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
        Generate the Carbon Reduction Plan required to bid for NHS and other UK public-sector contracts under
        PPN 006, in the Cabinet Office Annex A format. Each plan produces a PDF, a web page you can publish on
        your own website, and a spreadsheet of the underlying figures.
      </Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        The plan is prepared from your recorded emissions. Reviewing the figures, obtaining board or
        director-level sign-off, publishing the plan and keeping it updated at least annually remain your
        responsibility, and generating a plan does not by itself confer eligibility for any contract.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button
          variant='contained'
          startIcon={<AddOutlined sx={{ fontSize: 'small' }} />}
          onClick={() => navigate('/reporting/carbon-reduction-plan/new')}
          sx={{ textTransform: 'none' }}
        >
          <Typography sx={{ pl: 0.5 }}>Create new plan</Typography>
        </Button>
      </Box>

      <LoadingPage pageReady={!isLoading}>
        {rows.length === 0
          ? (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              You have not created a Carbon Reduction Plan yet.
            </Typography>
          )
          : (
            <TableDisplay
              headCells={headCells}
              rows={rows}
              isMobileMode={isMobileMode}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected}
              totalRecords={rows.length}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
      </LoadingPage>
    </PageOuterContainer>
  )
}
